import * as React from 'react';
import { useState } from 'react';

import { ChevronDown, ChevronRight } from 'lucide-react';

import { Divider, Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import {
  DATLocationMarketInfo,
  DATMarketInfo,
  QuickQuoteSlim,
} from 'types/agents/QuickQuoteAgent';

const MarketConditionValue = ({
  label,
  value,
}: {
  label: string;
  value: MaybeUndef<number | string>;
}): React.JSX.Element => (
  <Flex align='center' justify='between' className='w-full'>
    <Typography variant='body-xs' textColor='lessMuted'>
      {label}
    </Typography>
    <Typography variant='body-xs' weight='medium'>
      {value ?? '—'}
    </Typography>
  </Flex>
);

const LocationMarketInfo = ({
  label,
  info,
}: {
  label: string;
  info: DATLocationMarketInfo;
}): React.JSX.Element => (
  <Flex direction='col' gap='xs' className='w-full'>
    <Typography variant='body-xs' weight='semibold'>
      {label}: {info.stopLocation || '—'}
    </Typography>
    <div className='pl-2 border-l border-neutral-300 w-full'>
      <Flex direction='col' gap='none' className='w-full'>
        <MarketConditionValue
          label='Outbound L/T Ratio'
          value={info.outboundLoadToTruckRatio}
        />
        <MarketConditionValue
          label='Outbound MCI (prev day)'
          value={info.outboundMarketConditionsIndexPrevDay}
        />
        <MarketConditionValue
          label='Outbound MCI (prev week)'
          value={info.outboundMarketConditionsIndexPrevWeek}
        />
        <MarketConditionValue
          label='Outbound MCI Forecast (next day)'
          value={info.outboundMarketConditionsForecastNextDay}
        />
        <Divider size='sm' spacing='xs' className='w-full' />
        <MarketConditionValue
          label='Inbound L/T Ratio'
          value={info.inboundLoadToTruckRatio}
        />
        <MarketConditionValue
          label='Inbound MCI (prev day)'
          value={info.inboundMarketConditionsIndexPrevDay}
        />
        <MarketConditionValue
          label='Inbound MCI (prev week)'
          value={info.inboundMarketConditionsIndexPrevWeek}
        />
        <MarketConditionValue
          label='Inbound MCI Forecast (next day)'
          value={info.inboundMarketConditionsForecastNextDay}
        />
      </Flex>
    </div>
  </Flex>
);

/**
 * Renders a collapsible section showing raw DAT market information
 * (Load-to-Truck ratios, Market Conditions Index, forecasts) from associated quick quotes.
 */
export const DATMarketInfoSection = ({
  quickQuotes,
}: {
  quickQuotes?: QuickQuoteSlim[];
}): Maybe<React.JSX.Element> => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show if there's at least one quick quote with DAT market info
  const quotesWithMarketInfo = (quickQuotes ?? []).filter(
    (qq): qq is QuickQuoteSlim & { datMarketInfo: DATMarketInfo } =>
      qq.datMarketInfo != null
  );

  if (quotesWithMarketInfo.length === 0) {
    return null;
  }

  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <Flex direction='col' gap='xs' className='w-full mt-1'>
      <button
        type='button'
        onClick={() => setIsExpanded((prev) => !prev)}
        className='flex items-center gap-1 text-left cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 w-full'
      >
        <ChevronIcon className='w-3 h-3 text-neutral-500 shrink-0' />
        <Typography
          variant='body-sm'
          className='hover:text-neutral-500 hover:underline transition-colors'
        >
          DAT Market Information
        </Typography>
      </button>

      {isExpanded && (
        <Flex direction='col' gap='sm' className='w-full px-2 pt-1'>
          {quotesWithMarketInfo.map((qq, index) => (
            <div key={qq.id} className='w-full'>
              {index > 0 && (
                <Divider
                  size='sm'
                  spacing='sm'
                  variant='strong'
                  className='w-full'
                />
              )}
              <Flex direction='col' gap='sm' className='w-full pb-1'>
                {qq.datMarketInfo.pickupMarketInformation && (
                  <LocationMarketInfo
                    label='Pickup'
                    info={qq.datMarketInfo.pickupMarketInformation}
                  />
                )}
                {qq.datMarketInfo.dropoffMarketInformation && (
                  <LocationMarketInfo
                    label='Dropoff'
                    info={qq.datMarketInfo.dropoffMarketInformation}
                  />
                )}
              </Flex>
            </div>
          ))}
        </Flex>
      )}
    </Flex>
  );
};
